// Coaching application — the multi-step "Apply for coaching" form.
//
// ONE UNIFIED APPLICATION. Every applicant answers one question set (the
// UNIFIED FORM section at the bottom of this file), in the approved order and
// wording, and every row it saves carries
// `answers.form_version = APPLICATION_FORM_VERSION`. The steps a man sees
// depend only on his own answers: the done-for-you follow-up appears only for
// the dating-app work, a photo answer shows the matching buy-now product, a
// money answer below the program floor shows one cheaper option before the
// calendar, and "Not yet" to the show-up question ends on a product instead of
// the calendar.
//
// THE OLDER QUESTION SETS STAY, UNCHANGED, for the rows that answered them. A
// row without the version stamp resolves to the branch it was actually shown
// (the offer-branched default and Concierge sets and the money-experiment
// arms), so the team email, the lead workbench and the resume route render
// exactly what he was asked. No old answer is ever re-mapped onto a new
// question.
//
// Contact is always first (name + email together, so an abandoned application
// still leaves a reachable lead) and the booking is always last.
//
// This module is shared by the client form and the server route, so it must
// stay free of server-only imports (it is pure data + small helpers).

import { APPLICATION_FORM_VERSION } from "@/lib/application-form-version"
import { EMAIL_RE } from "@/lib/utils"

export type ApplicationFieldType =
  | "shorttext"
  | "longtext"
  | "email"
  | "number"
  | "single"
  // Multi-select: pick any number of options. Stored as a comma-joined string
  // of option values (keeps ApplicationAnswers a Record<string,string>), so the
  // payload, validation, and team email need no new shape.
  | "multi"
  // Two coupled age sliders, stored as "min-max" (e.g. "24-34"). Same
  // string-in-a-string trick as `multi`, for the same reason: the payload,
  // validation, and team email keep the shape they already had.
  | "agerange"

export interface ApplicationOption {
  value: string
  label: string
}

export interface ApplicationQuestion {
  /** Stable key — used in the payload and the team email. Never reuse. */
  id: string
  type: ApplicationFieldType
  /** The question itself. */
  label: string
  /** Sub-line under the question. */
  help?: string
  placeholder?: string
  required?: boolean
  /** For single-select. */
  options?: ApplicationOption[]
  /** Cap on free-text length. */
  maxLength?: number
  /**
   * Value recorded when the applicant moves on without touching the control.
   * Only used by inputs that show a meaningful value before any interaction
   * (the age sliders), so "he left the default alone" is stored as the default
   * rather than as nothing. Applied when the step advances, never in an effect.
   */
  defaultValue?: string
  /** The approved label already ends in "(optional)", so the form's own
   * Optional tag would say the same word twice. */
  labelSaysOptional?: boolean
}

export interface ApplicationStep {
  id: string
  /** Short uppercase label shown above the question. Internal readers (the
   * team emails, the drop-off funnel, the daily pulse) name the step by it
   * even when `hideEyebrow` keeps it off the visitor's screen. */
  eyebrow: string
  /** True when the eyebrow is an internal name only. The copy on /apply is
   * locked to his approved wording, and a step whose wording he approved
   * without an eyebrow shows none rather than a label nobody approved. */
  hideEyebrow?: boolean
  /** Usually one question; contact basics group a few. */
  questions: ApplicationQuestion[]
}

/**
 * ONE /apply that branches on which offer sent the visitor (owner-approved
 * 2026-07-27, per the rendered mockup at design/2026-07-26-application-branch/).
 *
 * It is deliberately one page and one form rather than two forked URLs: that is
 * what preserves the single lead-capture path, the `program` offer tag, and the
 * booking gate on the last step. The branch only decides which MIDDLE questions
 * a man answers. Both branches start on the same name + email step (the lead
 * capture) and end on the same investment → readiness → logistics → commitment
 * → booking spine.
 *
 * Every step below is a shared const so the two lists compose from the same
 * objects. A shared step edited once changes both branches, which is the point:
 * the shared spine can never silently drift apart.
 */

/**
 * The steps, in order. One question per screen (Typeform-style), except the
 * contact step which groups name/email/phone. Wording matches the house voice:
 * direct, conversational, benefit-led — no corporate intake-form tone.
 */
// Contact first, all on step 1 — name and email together, so every starter
// becomes a reachable lead the moment they move on (see captureLead in
// application-flow.tsx, which fires when this step completes). The phone
// question was removed to simplify the form (owner, 2026-07-03). SHARED by both
// branches: whichever offer sent him, the lead capture is the same first screen,
// because 84% of starters used to quit before a separate contact step.
const STEP_CONTACT: ApplicationStep = {
  id: "name",
  // Dead simple (owner, 2026-07-17: "you can just say name and email...
  // don't try to be fancy"): plain field labels, no question-style heading,
  // no clever step title, and no reassurance line about how short the form is
  // (removed everywhere - we rely on a man wanting coaching, not on the form
  // being quick).
  eyebrow: "Name and email",
  questions: [
    {
      id: "name",
      type: "shorttext",
      label: "Name",
      required: true,
      placeholder: "Your name",
      maxLength: 120,
    },
    {
      id: "email",
      type: "email",
      label: "Email",
      required: true,
      placeholder: "you@email.com",
    },
  ],
}

// THE ROUTING QUESTION. This question decides which of the two question sets a
// man sees next. Shared by both branches and placed directly after contact, so
// it is the first real question every applicant answers, whichever page he
// arrived from.
const STEP_ROUTE: ApplicationStep = {
  id: "route",
  eyebrow: "Where you want to improve",
  questions: [
    {
      id: "route",
      type: "single",
      label: "Where would you most like to improve your dating life?",
      required: true,
      options: [
        { value: "in_person", label: "Meeting women in person" },
        { value: "dating_apps", label: "Getting better results from dating apps" },
        { value: "both", label: "Both, I want more opportunities everywhere" },
        { value: "not_sure", label: "I'm not sure what's holding me back" },
      ],
    },
  ],
}

// ONE merged situation question (owner interview, 2026-07-21: consolidate the
// form). This absorbs the old "how often are you going out" (frequency)
// question — a guy who rarely approaches is also almost never going out, so
// the two were near-duplicates. Same option VALUES as before (no data
// migration), labels reworded to blend in the activity signal. The old
// frequency/tried questions moved to the application call. (The goal
// question was restored 2026-07-21 — see its step below.)
const STEP_EXPERIENCE: ApplicationStep = {
  id: "experience",
  eyebrow: "Where you're at",
  questions: [
    {
      id: "experience",
      type: "single",
      label: "Where are you at with meeting women right now?",
      required: true,
      options: [
        { value: "rarely", label: "I rarely go out or approach women" },
        { value: "lock_up", label: "I go out sometimes, but I lock up or fumble it" },
        { value: "stall", label: "I approach fine, but conversations or dates stall" },
        { value: "nowhere", label: "I get matches and dates, but they go nowhere" },
        { value: "experienced", label: "I'm experienced and want to sharpen specific things" },
      ],
    },
  ],
}

// Dating-goals question, restored to its original spot after the situation
// (owner, 2026-07-21: "add the question about their dating goals that we
// originally had back into the application form"). Multi-select with no
// subtitle (owner, 2026-07-18); values unchanged so historical rows resolve.
const STEP_GOAL: ApplicationStep = {
  id: "goal",
  eyebrow: "Your goal",
  questions: [
    {
      id: "goal",
      type: "multi",
      label: "What would make this a win for you?",
      required: true,
      options: [
        { value: "girlfriend", label: "A girlfriend I'm genuinely excited about" },
        { value: "options", label: "Real options: dates every week with women I choose" },
        { value: "skill_for_life", label: "The skill for life: walk up anywhere, anytime" },
        { value: "fix_sticking_point", label: "Fixing a specific sticking point for good" },
      ],
    },
  ],
}

// Money question right after the situation + goal (owner, 2026-07-11): the one
// qualifier that matters, so people who drop off later still answer it. SHARED
// and deliberately untouched by the branch work — it is the anchor of the whole
// form, and the form's real job is subtle financial qualification.
/**
 * THE OLD MONEY QUESTION. No live branch renders this any more; it survives so
 * the 35 historical rows that answered it still render their answer in the team
 * email and on the lead workbench, which map over the steps a row's own answers
 * resolve to. Delete it and that history silently stops displaying.
 *
 * It was replaced because it did not measure one thing. The label asked how
 * READY he was and the answers were DOLLARS, so 49% chose the bottom band -
 * which spanned nothing to $5,000 and therefore contained the entire
 * publicly-priced catalogue - and 20% chose $100,000+, which is not a number
 * anyone in this traffic is stating truthfully.
 */
const STEP_INVESTMENT_LEGACY: ApplicationStep = {
  id: "investment",
  eyebrow: "Investment",
  questions: [
    {
      id: "investment",
      type: "single",
      label: "How ready are you to invest in fixing this?",
      required: true,
      options: [
        { value: "under_5000", label: "Under $5,000" },
        { value: "5000_20000", label: "$5,000 to $20,000" },
        { value: "20000_50000", label: "$20,000 to $50,000" },
        { value: "50000_100000", label: "$50,000 to $100,000" },
        { value: "100000_plus", label: "$100,000+" },
      ],
    },
  ],
}

/**
 * ARM A of the money experiment: state the range, then ask how he would pay it.
 * The help line names the whole ladder, a single $200 call up to programs from
 * $3,000, so a man below the program floor still sees a way in (owner: show
 * the full range rather than only the floor).
 *
 * Four states, all of them about money and nothing else. An earlier draft had
 * "I could be ready within 30 days" as a third option, which put a TIMING
 * answer inside the variable under test; when he could start is already asked
 * by STEP_COMMITMENT and stays there.
 */
const STEP_MONEY_PRICE: ApplicationStep = {
  id: "money",
  eyebrow: "Investment",
  questions: [
    {
      id: "money_fit",
      type: "single",
      label: "If we both decide it's the right fit, which sounds like you?",
      help: "We build the program around where you are and what you need. Coaching runs from a single $200 call up to full programs from $3,000, with payment options available.",
      required: true,
      options: [
        { value: "pay_in_full", label: "I could pay in full" },
        { value: "payment_option", label: "I'd prefer a payment option" },
        { value: "need_time", label: "I'd need some time before I could invest $3,000" },
        { value: "not_realistic", label: "$3,000 isn't realistic for me right now" },
      ],
    },
  ],
}

/**
 * ARM B: no price, and ask what is realistic for him NOW.
 *
 * The top band opens at $20,000 because the real ladder is close above it - the
 * 64 live Stripe payment links run $100 to $30,000, and Mentorship closes on
 * that ladder. Every boundary here is therefore a boundary we can actually
 * sell across, which the old $100,000+ option was not.
 */
const STEP_MONEY_BUDGET: ApplicationStep = {
  id: "money",
  eyebrow: "Investment",
  questions: [
    {
      id: "money_budget",
      type: "single",
      label:
        "If you found the right solution, what level of investment would be realistic for you right now?",
      required: true,
      options: [
        { value: "under_3000", label: "Under $3,000" },
        { value: "3000_5000", label: "$3,000 to $5,000" },
        { value: "5000_10000", label: "$5,000 to $10,000" },
        { value: "10000_20000", label: "$10,000 to $20,000" },
        { value: "over_20000", label: "More than $20,000" },
      ],
    },
  ],
}

/**
 * A CONSTANT in both arms, deliberately not an arm of its own. Past spend is
 * the one genuinely behavioural signal on the form, and keeping it identical
 * across both arms means the only thing that varies is whether we showed a
 * price - which is the whole question the experiment asks.
 *
 * It is evidence, never the qualifier. A man can have spent $10,000 two years
 * ago and be broke today, and another can have spent nothing and write a cheque
 * this afternoon.
 */
const STEP_PRIOR_SPEND: ApplicationStep = {
  id: "prior_spend",
  eyebrow: "What you've spent",
  questions: [
    {
      id: "prior_spend",
      type: "single",
      label: "What have you already spent trying to fix this?",
      required: true,
      options: [
        { value: "nothing", label: "Nothing yet" },
        { value: "under_500", label: "Under $500" },
        { value: "500_2000", label: "$500 to $2,000" },
        { value: "over_2000", label: "More than $2,000" },
      ],
    },
  ],
}

// ONE merged readiness question (owner interview, 2026-07-21): this absorbs
// the old "how soon do you want to start" (timeline) question — someone who's
// "all in" is usually "asap", so the two were near-duplicates. Same option
// VALUES as the old commitment question (no data migration), label + options
// reworded to blend in the timing/urgency signal.
const STEP_COMMITMENT: ApplicationStep = {
  id: "commitment",
  eyebrow: "How ready you are",
  questions: [
    {
      id: "commitment",
      type: "single",
      label: "How ready are you to start?",
      required: true,
      options: [
        { value: "all_in", label: "All in, I want to start now" },
        { value: "ready_plan", label: "Ready, I just want a clear plan to follow" },
        { value: "deciding", label: "Curious, still deciding" },
      ],
    },
  ],
}

const STEP_LOGISTICS: ApplicationStep = {
  id: "logistics",
  eyebrow: "Logistics",
  questions: [
    {
      id: "city",
      type: "shorttext",
      label: "What city are you in?",
      placeholder: "Your city",
      required: true,
      maxLength: 120,
    },
    {
      id: "age",
      type: "single",
      label: "How old are you?",
      required: true,
      options: [
        { value: "under_25", label: "Under 25" },
        { value: "25_29", label: "25 to 29" },
        { value: "30_34", label: "30 to 34" },
        { value: "35_39", label: "35 to 39" },
        { value: "40_49", label: "40 to 49" },
        { value: "50_plus", label: "50 or older" },
      ],
    },
  ],
}

// THE SHOW-UP COMMITMENT, second to last by design (owner, 2026-07-25, from a
// competitor's form he wanted the equivalent of).
//
// It does two jobs at once. It tells the applicant what actually happens next,
// which the form never said out loud, and it asks him to commit before he sees
// the calendar. Asking for the commitment BEFORE the booking screen is the
// whole trick: a man who has just typed "yes I will show up" behaves
// differently at the calendar than one who wandered onto it.
//
// Why the wording is ours and not the competitor's: theirs leans on "300+
// clients" and "100% certainty". The owner-approved true count is HUNDREDS of
// students coached (context/voice-and-copy.md, 2026-07-24), and a
// certainty claim about someone's dating results is exactly the unsubstantiable
// outcome promise the guarantee posture bans. So the proof stays real and the
// promise stays absent.
//
// The "maybe" option is deliberately kept and deliberately unflattering. A
// one-option commitment question is theatre; the answer only carries
// information if a man can say no, and a "maybe" here is a genuinely useful
// signal on the lead rather than a lost application.
const STEP_SHOW_UP: ApplicationStep = {
  id: "show_up",
  eyebrow: "Last question",
  questions: [
    {
      id: "show_up",
      type: "single",
      label: "Will you show up to the call you book?",
      help: "Next you pick a time. We have coached hundreds of students, and we cannot help you if you do not show up. Pick a time you can take somewhere quiet, ready to work.",
      required: true,
      options: [
        { value: "committed", label: "Yes. I will check my calendar and show up." },
        { value: "unsure", label: "Maybe. I am not sure I am serious yet." },
      ],
    },
  ],
}

// THE BOOKING, the final step INSIDE the form (owner, 2026-07-25).
//
// It used to live on a separate page after Submit, and the numbers say that
// was the leak: of 11 completed applications in the 90 days to 2026-07-25,
// TEN never booked a call. His read is that pressing Submit felt like
// finishing, so the call looked optional. Making the calendar the last step
// removes the moment where a man can believe he is done.
//
// It carries no `questions`, which is deliberate — the step renders the Cal
// embed instead, and `getStepValidation` blocks it until a booking actually
// confirms. This is NOT the iframed page-inside-a-page that was rightly
// rejected on 2026-07-18: it is the booking widget itself, mounted inline,
// with a link out to the real /book page for anyone whose browser blocks the
// embed. /book stays exactly as it was, because it is linked from emails.
const STEP_BOOKING: ApplicationStep = {
  id: "booking",
  eyebrow: "Book your call",
  questions: [],
}

// ---------------------------------------------------------------------------
// Online Dating Concierge only. These four replace the two in-person
// coaching questions, because the service is different: we run his apps, so we
// need to know what his apps are doing for him today and who we are supposed to
// be finding, and neither of those is answered by "where are you at with
// meeting women".
// ---------------------------------------------------------------------------

// The DFY equivalent of the `experience` question. It asks the same thing —
// where are you starting from — in the only terms that matter for a service
// that runs his dating apps.
const STEP_APP_ACTIVITY: ApplicationStep = {
  id: "app_activity",
  eyebrow: "Where you're at",
  questions: [
    {
      id: "app_activity",
      type: "single",
      label: "How many dates do your dating apps get you right now?",
      required: true,
      options: [
        { value: "none", label: "None. I barely get matches." },
        { value: "monthly", label: "One or two a month, and not with women I want." },
        { value: "weekly", label: "About one a week." },
        { value: "often", label: "Two or more a week, but I want better quality." },
      ],
    },
  ],
}

// Income, asked because the DFY flagship is priced on the call and this is the
// same subtle financial qualification the investment question does.
const STEP_INCOME: ApplicationStep = {
  id: "income",
  eyebrow: "Investment",
  questions: [
    {
      id: "income",
      type: "single",
      label: "What's your yearly income right now?",
      required: true,
      options: [
        { value: "under_100k", label: "Under $100,000" },
        { value: "100k_250k", label: "$100,000 to $250,000" },
        { value: "250k_500k", label: "$250,000 to $500,000" },
        { value: "500k_plus", label: "$500,000+" },
      ],
    },
  ],
}

// What he has already tried, in dating-app terms. Tells a coach on the call
// whether he has already paid for photos or premium and still got nowhere,
// which is the strongest signal that the problem is the whole profile.
const STEP_TRIED: ApplicationStep = {
  id: "tried",
  eyebrow: "What you've tried",
  questions: [
    {
      id: "tried",
      type: "multi",
      label: "What have you already tried?",
      required: true,
      options: [
        { value: "premium_apps", label: "Paid for premium features on the apps" },
        { value: "photos", label: "Had my photos done professionally" },
        { value: "ai_profile", label: "Used AI to write my profile" },
        { value: "advice", label: "Watched dating advice online" },
        { value: "coach", label: "Hired a coach before" },
        { value: "nothing", label: "Nothing specific yet" },
      ],
    },
  ],
}

// Who we are swiping for. This question exists only because the service does
// the swiping and the texting, so his type is an operational input, not a
// curiosity. Both parts are optional: the sliders carry a sensible default and
// the preferences are a light touch, so this step never blocks anyone.
const STEP_MATCH_TYPE: ApplicationStep = {
  id: "match_type",
  eyebrow: "Who we're finding",
  questions: [
    {
      id: "match_age",
      type: "agerange",
      label: "How old should she be?",
      help: "We run your swiping, so we work inside the range you set here.",
      defaultValue: "24-34",
    },
    {
      id: "match_priorities",
      type: "multi",
      label: "What matters most to you?",
      options: [
        { value: "active", label: "Athletic or active" },
        { value: "family", label: "Family-oriented" },
        { value: "career", label: "Career-driven" },
        { value: "similar", label: "Similar interests" },
        { value: "none", label: "No strong preference" },
      ],
    },
  ],
}

/**
 * THE HISTORICAL BRANCHES. Before the unified form, a man answered one of two
 * middle question sets, resolved from his routing answer and the `program`
 * offer tag. The live form no longer branches; these resolve only a row saved
 * without the unified version stamp, so it renders what it was asked.
 */
// TWO question sets, and only two (owner, 2026-08-22): "only the mentorship and
// concierge services actually require applications... bootcamp is purchased,
// one-on-one calls are purchased, online dating mentorship is a direct
// purchase, and profile makeover and AI photos are direct purchase. So that
// means you only need two sets of questions. The default mentorship program is
// the default finder program. And then online dating concierge is another set."
//
// There used to be a third branch name, `mentorship`, whose step list was a
// literal alias of the default and therefore changed nothing. A branch that
// renders the same questions is not a branch; it is a second name for one, and
// it made the form look more forked than it was.
export type ApplicationBranch = "default" | "dfy"

/**
 * The three vertical Mentorship pages' acquisition contexts (his 2026-09-01
 * build order: "one adaptive Mentorship application", never three forms). A
 * page supplies its focus via /apply?focus=<value>; the applicant then sees
 * ONE context-specific diagnostic step instead of the generic routing +
 * meeting-women questions ("the current generic question about meeting women
 * in person versus dating apps should not appear unchanged to relationship or
 * breakup prospects"), and rejoins the shared money/commitment/logistics/
 * booking spine. Stored on the row as `answers.mentorship_focus`.
 */
export type MentorshipFocus = "post_breakup" | "relationship_crisis" | "social_circle"

export function isMentorshipFocus(value: unknown): value is MentorshipFocus {
  return value === "post_breakup" || value === "relationship_crisis" || value === "social_circle"
}

// The per-focus diagnostic steps. Copy is his pasted spec's, options given
// stable snake_case values. One step per focus (post-breakup carries the
// spec's two questions on its one screen).
const STEP_FOCUS_POST_BREAKUP: ApplicationStep = {
  id: "focus_post_breakup",
  eyebrow: "Your breakup",
  questions: [
    {
      id: "breakup_outcome",
      type: "single",
      label: "What outcome are you currently hoping for?",
      required: true,
      options: [
        { value: "rebuild", label: "Rebuild the relationship with my ex" },
        { value: "understand_reconciliation", label: "Understand whether reconciliation makes sense" },
        { value: "move_forward", label: "Move forward and start dating again" },
        { value: "not_sure", label: "I genuinely do not know yet" },
      ],
    },
    {
      id: "breakup_since",
      type: "single",
      label: "What has happened since the breakup?",
      required: true,
      options: [
        { value: "no_contact", label: "No contact" },
        { value: "occasional_contact", label: "Occasional contact" },
        { value: "regular_contact", label: "Regular contact" },
        { value: "blocked_ignored", label: "She has blocked or ignored me" },
        { value: "seeing_someone_else", label: "She is seeing someone else" },
        { value: "tried_reconciling", label: "We have already tried reconciling" },
      ],
    },
  ],
}

const STEP_FOCUS_RELATIONSHIP: ApplicationStep = {
  id: "focus_relationship",
  eyebrow: "Your relationship",
  questions: [
    {
      id: "relationship_problem",
      type: "single",
      label: "What is the main problem right now?",
      required: true,
      options: [
        { value: "repeated_conflict", label: "Repeated conflict" },
        { value: "emotional_distance", label: "Emotional distance" },
        { value: "lost_intimacy", label: "Loss of attraction or intimacy" },
        { value: "broken_trust", label: "Broken trust" },
        { value: "jealousy_control_boundaries", label: "Jealousy, control, or boundaries" },
        { value: "separation_raised", label: "Separation or divorce has been raised" },
        { value: "unsure_stay", label: "I do not know whether to stay" },
      ],
    },
  ],
}

const STEP_FOCUS_SOCIAL: ApplicationStep = {
  id: "focus_social_circle",
  eyebrow: "Your social life",
  questions: [
    {
      id: "social_gap",
      type: "single",
      label: "What is missing from your social life?",
      required: true,
      options: [
        { value: "know_nobody", label: "I barely know anyone in my city" },
        { value: "no_real_circle", label: "I have acquaintances but no real circle" },
        { value: "no_invitations", label: "I rarely receive invitations" },
        { value: "never_host", label: "I do not host or organize anything" },
        { value: "app_reliant", label: "I rely too heavily on dating apps or cold approach" },
        { value: "beyond_dating", label: "I want a stronger social life even outside dating" },
      ],
    },
  ],
}

const FOCUS_STEPS: Record<MentorshipFocus, ApplicationStep> = {
  post_breakup: STEP_FOCUS_POST_BREAKUP,
  relationship_crisis: STEP_FOCUS_RELATIONSHIP,
  social_circle: STEP_FOCUS_SOCIAL,
}

/**
 * The historical default path, which a row saved before the unified form
 * resolves to when neither its routing answer nor its offer tag put it on the
 * Concierge set.
 */
/** Which money screen a man saw. Stamped on the row, never inferred later. */
export type MoneyArm = "price_disclosed" | "budget_bands"

export function isMoneyArm(value: unknown): value is MoneyArm {
  return value === "price_disclosed" || value === "budget_bands"
}

/**
 * The money block for an arm. Both arms are ONE step carrying one question, and
 * both use the step id "money", so step COUNT and step IDS are identical across
 * arms and only the question inside differs. That is what lets the drop-off
 * funnel, the daily-pulse retention table and the abandoned-lead progress
 * percentage keep counting without knowing the experiment exists.
 *
 * With no arm the block is the single legacy step and nothing else, so a row
 * from before the experiment renders exactly the questions it was actually
 * asked rather than growing an unanswered prior-spend row.
 */
function moneyBlock(arm: MoneyArm | null): ApplicationStep[] {
  if (!arm) return [STEP_INVESTMENT_LEGACY]
  return [arm === "price_disclosed" ? STEP_MONEY_PRICE : STEP_MONEY_BUDGET, STEP_PRIOR_SPEND]
}

function defaultStepsFor(arm: MoneyArm | null): ApplicationStep[] {
  return [
    STEP_CONTACT,
    STEP_ROUTE,
    STEP_EXPERIENCE,
    STEP_GOAL,
    ...moneyBlock(arm),
    STEP_COMMITMENT,
    STEP_LOGISTICS,
    STEP_SHOW_UP,
    STEP_BOOKING,
  ]
}

/**
 * The Online Dating Concierge path. Same opening step, same closing spine,
 * different middle: what his apps do for him now, his income, what he has
 * already tried, and who we are looking for.
 */
function dfyStepsFor(arm: MoneyArm | null): ApplicationStep[] {
  return [
    STEP_CONTACT,
    STEP_ROUTE,
    STEP_APP_ACTIVITY,
    STEP_INCOME,
    STEP_TRIED,
    STEP_MATCH_TYPE,
    ...moneyBlock(arm),
    STEP_COMMITMENT,
    STEP_LOGISTICS,
    STEP_SHOW_UP,
    STEP_BOOKING,
  ]
}

/** The representative historical Concierge list, for rows saved before the
 * unified form. */
export const DFY_APPLICATION_STEPS: ApplicationStep[] = dfyStepsFor("price_disclosed")

/**
 * A vertical page's focus path: the shared contact step, that focus's ONE
 * diagnostic step, then straight into the shared money/commitment/logistics/
 * booking spine. Deliberately NO routing question and NO meeting-women
 * experience/goal questions - a breakup or relationship prospect's problem is
 * not "where would you most like to improve your dating life", and the spec
 * bans showing him that framing unchanged.
 */
function focusStepsFor(focus: MentorshipFocus, arm: MoneyArm | null): ApplicationStep[] {
  return [
    STEP_CONTACT,
    FOCUS_STEPS[focus],
    ...moneyBlock(arm),
    STEP_COMMITMENT,
    STEP_LOGISTICS,
    STEP_SHOW_UP,
    STEP_BOOKING,
  ]
}

/**
 * Which done-for-you offer tags still land on the Done-For-You question set
 * when the routing question below doesn't decide it directly. "profile-makeover"
 * is the stored tag for Online Dating Concierge (the page kept its old URL
 * through the rename, so the tag kept its old value); "ai-photos" is the AI
 * Photos tag. Everything else - "mentorship" included, on purpose, since it is
 * the finder path and not a fork - falls to the default set.
 */
const DFY_PROGRAM_TAGS = new Set<string>(["profile-makeover", "ai-photos"])

/**
 * Which branch a man sees, resolved from the ROUTING QUESTION first and the
 * offer tag second. "Meeting women in person" is the coaching set; "Getting
 * better results from dating apps" is the Done-For-You set. "Both", "I'm not sure", or no routing answer
 * at all - a row saved before this question existed - fall back to the offer
 * tag that sent him here: a done-for-you tag (`DFY_PROGRAM_TAGS`) still lands
 * on the Done-For-You set, and anything else, including a bare untagged
 * visit, gets the default path. A guess is never made beyond that fallback.
 */
export function applicationBranchForProgram(
  routeAnswer: string | null | undefined,
  program?: string | null | undefined,
): ApplicationBranch {
  switch ((routeAnswer ?? "").trim()) {
    case "in_person":
      return "default"
    case "dating_apps":
      return "dfy"
    default:
      return DFY_PROGRAM_TAGS.has((program ?? "").trim()) ? "dfy" : "default"
  }
}

/** The steps a given branch renders, in order. */
export function getApplicationSteps(
  branch: ApplicationBranch,
  arm: MoneyArm | null = "price_disclosed",
): ApplicationStep[] {
  return branch === "dfy" ? dfyStepsFor(arm) : defaultStepsFor(arm)
}

/**
 * The steps a stored answer set was ACTUALLY shown. Every surface that renders
 * answers must come through here.
 *
 * A row stamped with the unified form version resolves to the unified steps,
 * which depend only on its own answers. A row without the stamp predates the
 * unified form and resolves from its own `program` tag and its own
 * `money_arm`: an arm-blind step list would show a price-arm applicant the
 * budget-arm question he never saw, and would drop the answer he did give. A
 * legacy row with no arm resolves to the legacy money step, which is what every
 * row before the experiment was asked.
 */
export function applicationStepsForAnswers(answers: ApplicationAnswers): ApplicationStep[] {
  if (isUnifiedApplication(answers)) return unifiedStepsFor(answers)
  const arm = isMoneyArm(answers.money_arm) ? answers.money_arm : null
  // A vertical page's focus OWNS the row it stamped: the man never saw the
  // routing question, so resolving through the branch would render questions
  // he was never asked and hide the diagnostic he did answer.
  if (isMentorshipFocus(answers.mentorship_focus)) {
    return focusStepsFor(answers.mentorship_focus, arm)
  }
  return getApplicationSteps(applicationBranchForProgram(answers.route, answers.program), arm)
}

// ===========================================================================
// THE UNIFIED FORM, the one question set every applicant answers.
//
// Every visible string below is his approved wording, word for word. A step
// whose approved wording carries no eyebrow keeps its eyebrow as an internal
// name only (`hideEyebrow`).
// ===========================================================================

/** The version stamp every unified row carries in `answers.form_version`,
 * defined in its own module so the site-wide analytics module can read it
 * without loading this question set (see web/lib/application-form-version.ts). */
export { APPLICATION_FORM_VERSION }

/** True when an answer set was saved by the unified form. */
export function isUnifiedApplication(answers: ApplicationAnswers): boolean {
  return (answers.form_version ?? "").trim() === APPLICATION_FORM_VERSION
}

/** Where he wants better results. Stored under the historical key `route`,
 * with the same four values and the same meaning as before, so every reader of
 * that key keeps working; the question and its labels are new, and a unified
 * row renders them while a historical row renders the question it was asked. */
const STEP_WHERE: ApplicationStep = {
  id: "route",
  eyebrow: "Where you want to improve",
  questions: [
    {
      id: "route",
      type: "single",
      label: "Where do you want better results?",
      required: true,
      options: [
        { value: "in_person", label: "Meeting women in person" },
        { value: "dating_apps", label: "On dating apps" },
        { value: "both", label: "Both" },
        { value: "not_sure", label: "I'm not sure yet" },
      ],
    },
  ],
}

/** How he wants help. It is asked of everyone and decides nothing about which
 * program he is applying for: the call still finds the program. It only
 * decides whether the done-for-you follow-up appears. */
const STEP_HELP_HOW: ApplicationStep = {
  id: "help_how",
  eyebrow: "How you want help",
  hideEyebrow: true,
  questions: [
    {
      id: "help_how",
      type: "single",
      label: "How would you like us to help?",
      required: true,
      options: [
        { value: "coach_me", label: "Coach me so I get better at it myself" },
        { value: "dating_app_work", label: "Do the dating app work for me" },
        { value: "mix", label: "A mix of both" },
        { value: "help_choose", label: "I'm not sure yet, help me choose" },
      ],
    },
  ],
}

/** The done-for-you follow-up, only for "Do the dating app work for me" and
 * "A mix of both". */
const STEP_DFY_SCOPE: ApplicationStep = {
  id: "dfy_scope",
  eyebrow: "What we take care of",
  hideEyebrow: true,
  questions: [
    {
      id: "dfy_scope",
      type: "single",
      label: "What would you like us to take care of?",
      required: true,
      options: [
        { value: "photos_only", label: "Just new photos" },
        { value: "photos_profile", label: "New photos and a new profile" },
        { value: "all_of_it", label: "All of it: photos, profile, and running my apps for me" },
        { value: "not_sure", label: "I'm not sure yet" },
      ],
    },
  ],
}

/** Shown after "Just new photos" (AI Photos) or "New photos and a new
 * profile" (Profile Makeover): the product page to buy, and a small link that
 * carries on with the application. It asks nothing. */
const STEP_DFY_PRODUCT: ApplicationStep = {
  id: "dfy_product",
  eyebrow: "Recommended product",
  hideEyebrow: true,
  questions: [],
}

const STEP_MAIN_PROBLEM: ApplicationStep = {
  id: "main_problem",
  eyebrow: "Where you're at",
  questions: [
    {
      id: "main_problem",
      type: "single",
      label: "What's the main thing holding you back right now?",
      required: true,
      options: [
        { value: "not_meeting", label: "I don't get out and meet many women" },
        { value: "nervous", label: "I see women I like, but I get nervous and don't go talk to them" },
        { value: "no_dates", label: "I can talk to women, but it doesn't turn into dates" },
        { value: "app_matches", label: "I'm not getting good matches on dating apps" },
        { value: "texts_nowhere", label: "I get numbers or matches, but my texts go nowhere" },
        { value: "breakup_relationship", label: "A breakup, or problems in my relationship" },
        { value: "something_else", label: "Something else" },
      ],
    },
  ],
}

/** The seven money answers. Routing: every "comfortably" answer and the
 * stretch answer book straight away; "~$200" sees the $200 1-on-1 Call first
 * and "can't invest much" sees Community first, and both can still continue to
 * book. Everyone sees the price range. */
const STEP_MONEY_BAND: ApplicationStep = {
  id: "money",
  eyebrow: "Investment",
  questions: [
    {
      id: "money_band",
      type: "single",
      label: "Can you comfortably invest in fixing this?",
      help: "Coaching runs from a single $200 call up to full programs from $3,000, with payment options available.",
      required: true,
      options: [
        { value: "over_20000", label: "I can comfortably invest more than $20,000" },
        { value: "10000_20000", label: "I can comfortably invest $10,000 to $20,000" },
        { value: "5000_10000", label: "I can comfortably invest $5,000 to $10,000" },
        { value: "3000_5000", label: "I can comfortably invest $3,000 to $5,000" },
        { value: "stretch_3000", label: "It's a stretch, but $3,000 isn't out of my budget" },
        { value: "around_200", label: "That's out of budget, but I could invest ~$200 in myself" },
        { value: "cant_invest", label: "I can't invest much right now" },
      ],
    },
  ],
}

/** The start window, in place of the readiness question. */
const STEP_START_WINDOW: ApplicationStep = {
  id: "start_window",
  eyebrow: "How ready you are",
  questions: [
    {
      id: "start_window",
      type: "single",
      label: "If it's the right fit, when could you start?",
      required: true,
      options: [
        { value: "right_away", label: "Right away" },
        { value: "within_month", label: "Within the next month" },
        { value: "one_to_three_months", label: "In 1 to 3 months" },
        { value: "just_looking", label: "I'm just looking into it for now" },
      ],
    },
  ],
}

/** City and age. The youngest band reads "18 to 24" and is stored as its own
 * value, so a historical "Under 25" answer keeps reading "Under 25". */
const STEP_LOGISTICS_UNIFIED: ApplicationStep = {
  id: "logistics",
  eyebrow: "Logistics",
  questions: [
    STEP_LOGISTICS.questions[0],
    {
      id: "age",
      type: "single",
      label: "How old are you?",
      required: true,
      options: [
        { value: "18_24", label: "18 to 24" },
        { value: "25_29", label: "25 to 29" },
        { value: "30_34", label: "30 to 34" },
        { value: "35_39", label: "35 to 39" },
        { value: "40_49", label: "40 to 49" },
        { value: "50_plus", label: "50 or older" },
      ],
    },
  ],
}

/** The optional context box, its own screen right before the show-up
 * question. Continue works when it is empty. The hint is visible text under
 * the label, never a placeholder that vanishes on the first tap. */
const STEP_CONTEXT_NOTE: ApplicationStep = {
  id: "context_note",
  eyebrow: "Anything else",
  hideEyebrow: true,
  questions: [
    {
      id: "context_note",
      type: "longtext",
      label: "Anything else you'd like us to know? (optional)",
      help: "For example, a recent breakup, trouble in your relationship, or something we didn't ask.",
      labelSaysOptional: true,
      maxLength: 2000,
    },
  ],
}

/** The show-up answer that keeps the calendar closed. */
export const SHOW_UP_NOT_YET = "not_yet"

/** The show-up question. "Not yet" ends on one product instead of the
 * calendar, and the booking route refuses a booking for a row whose latest
 * answer is "Not yet". A man who goes back and changes it to "Yes" books like
 * anyone else: his latest answer is the one that counts.
 *
 * It carries NO help line. His approved wording for this screen is the
 * question and its two answers, nothing else, and the historical help line
 * ("Next you pick a time...") is untrue here: a "Not yet" never picks a time,
 * and a man below the program floor sees a product before the calendar. */
const STEP_SHOW_UP_UNIFIED: ApplicationStep = {
  id: "show_up",
  eyebrow: "Last question",
  questions: [
    {
      id: "show_up",
      type: "single",
      label: "Will you show up to the call you book?",
      required: true,
      options: [
        { value: "committed", label: "Yes. I will check my calendar and show up." },
        { value: SHOW_UP_NOT_YET, label: "Not yet. I'm not ready to commit to a call." },
      ],
    },
  ],
}

/** The cheaper option a man below the program floor sees before the calendar,
 * with a small link that carries on to the booking. It asks nothing. */
const STEP_MONEY_PRODUCT: ApplicationStep = {
  id: "money_product",
  eyebrow: "Recommended product",
  hideEyebrow: true,
  questions: [],
}

/** Where "Not yet" ends: one product, no calendar, no booking button. */
const STEP_NOT_READY: ApplicationStep = {
  id: "not_ready",
  eyebrow: "Not ready yet",
  hideEyebrow: true,
  questions: [],
}

/** The buy-now products the application can point a man at. */
export type ApplyProduct = "one_on_one_call" | "community" | "ai_photos" | "profile_makeover"

export const APPLY_PRODUCTS: readonly ApplyProduct[] = [
  "one_on_one_call",
  "community",
  "ai_photos",
  "profile_makeover",
]

/** What a product card shows. Built on the server by the /apply page from the
 * product's own checkout copy, its live price and its real page, so the form
 * carries no product wording of its own and never states a price checkout
 * does not charge. */
export type ApplyProductCard = {
  title: string
  subtitle: string
  price: string
  href: string
}

/** The steps that show a product instead of asking a question. A row whose
 * last saved step is one of these was sent to a product, so it is never chased
 * with a finish-your-application email. */
export const PRODUCT_RECOMMENDATION_STEP_IDS: ReadonlySet<string> = new Set([
  STEP_DFY_PRODUCT.id,
  STEP_MONEY_PRODUCT.id,
  STEP_NOT_READY.id,
])

/** True when the application's last saved step is a product recommendation. */
export function isProductRecommendationStep(stepId: string | null | undefined): boolean {
  return PRODUCT_RECOMMENDATION_STEP_IDS.has((stepId ?? "").trim())
}

/** The money answers at or above the $3,000 program floor, the stretch answer
 * included, since he says $3,000 is within reach. */
const AT_PROGRAM_FLOOR = new Set(["over_20000", "10000_20000", "5000_10000", "3000_5000", "stretch_3000"])

/** True when he asked for the dating-app work, alone or mixed with coaching. */
function wantsDatingAppWork(answers: ApplicationAnswers): boolean {
  const how = (answers.help_how ?? "").trim()
  return how === "dating_app_work" || how === "mix"
}

/** His done-for-you scope, only while the follow-up is actually on his path. A
 * scope left over from an earlier answer he has since changed means nothing. */
function activeDfyScope(answers: ApplicationAnswers): string | null {
  if (isMentorshipFocus(answers.mentorship_focus) || !wantsDatingAppWork(answers)) return null
  return (answers.dfy_scope ?? "").trim() || null
}

/** The product the photo answers point at: AI Photos for photos only, the
 * Profile Makeover for photos and a new profile. */
export function dfyProductFor(answers: ApplicationAnswers): ApplyProduct | null {
  const scope = activeDfyScope(answers)
  if (scope === "photos_only") return "ai_photos"
  if (scope === "photos_profile") return "profile_makeover"
  return null
}

/** The cheaper option a man who said he will show up sees before the
 * calendar: the $200 1-on-1 Call for "~$200", Community for "can't invest
 * much", nothing for everyone else. */
export function cheaperOptionFor(answers: ApplicationAnswers): ApplyProduct | null {
  const money = (answers.money_band ?? "").trim()
  if (money === "around_200") return "one_on_one_call"
  if (money === "cant_invest") return "community"
  return null
}

/** The one product the "Not yet" screen shows, checked top to bottom:
 * can't invest much -> Community; photos only -> AI Photos; more than photos
 * done and $3,000+ -> Profile Makeover; more than photos done but below
 * $3,000 -> AI Photos; everyone else -> the $200 1-on-1 Call. */
export function notReadyProductFor(answers: ApplicationAnswers): ApplyProduct {
  const money = (answers.money_band ?? "").trim()
  if (money === "cant_invest") return "community"
  const scope = activeDfyScope(answers)
  if (scope === "photos_only") return "ai_photos"
  if (scope === "photos_profile" || scope === "all_of_it") {
    return AT_PROGRAM_FLOOR.has(money) ? "profile_makeover" : "ai_photos"
  }
  return "one_on_one_call"
}

/** The product a product step shows for these answers, or null for a step
 * that is not a product step. */
export function recommendedProductFor(stepId: string, answers: ApplicationAnswers): ApplyProduct | null {
  if (stepId === STEP_DFY_PRODUCT.id) return dfyProductFor(answers)
  if (stepId === STEP_MONEY_PRODUCT.id) return cheaperOptionFor(answers)
  if (stepId === STEP_NOT_READY.id) return notReadyProductFor(answers)
  return null
}

/** True when this row's latest show-up answer keeps the calendar closed. */
export function showUpBlocksBooking(answers: unknown): boolean {
  if (!answers || typeof answers !== "object" || Array.isArray(answers)) return false
  const showUp = (answers as Record<string, unknown>).show_up
  return typeof showUp === "string" && showUp.trim() === SHOW_UP_NOT_YET
}

/**
 * The unified steps for one answer set, in his approved order:
 * name and email, where, how, (the done-for-you follow-up and its product),
 * the main problem, the win, money, start window, city and age, the optional
 * context box, show-up, then either the "Not yet" product or (the cheaper
 * option and) the booking.
 *
 * A vertical Mentorship page's visit (`mentorship_focus`) keeps its one
 * diagnostic step in place of where, how, the main problem and the win, and
 * then joins the same closing spine, so the money routing, the context box and
 * the show-up gate apply to him exactly as to everyone else.
 */
function unifiedStepsFor(answers: ApplicationAnswers): ApplicationStep[] {
  const steps: ApplicationStep[] = [STEP_CONTACT]
  if (isMentorshipFocus(answers.mentorship_focus)) {
    steps.push(FOCUS_STEPS[answers.mentorship_focus])
  } else {
    steps.push(STEP_WHERE, STEP_HELP_HOW)
    if (wantsDatingAppWork(answers)) {
      steps.push(STEP_DFY_SCOPE)
      if (dfyProductFor(answers)) steps.push(STEP_DFY_PRODUCT)
    }
    steps.push(STEP_MAIN_PROBLEM, STEP_GOAL)
  }
  steps.push(STEP_MONEY_BAND, STEP_START_WINDOW, STEP_LOGISTICS_UNIFIED, STEP_CONTEXT_NOTE, STEP_SHOW_UP_UNIFIED)
  if (showUpBlocksBooking(answers)) {
    steps.push(STEP_NOT_READY)
  } else {
    if (cheaperOptionFor(answers)) steps.push(STEP_MONEY_PRODUCT)
    steps.push(STEP_BOOKING)
  }
  return steps
}

/**
 * The representative live list: the unified path a man takes when nothing
 * inserts a step (no done-for-you follow-up, no product screen). Safe for a
 * consumer that only counts steps or names them; a consumer that renders
 * ANSWERS must still go through `applicationStepsForAnswers`, which resolves
 * the steps the man actually saw.
 */
export const APPLICATION_STEPS: ApplicationStep[] = unifiedStepsFor({ form_version: APPLICATION_FORM_VERSION })

/**
 * Every step any version of the form can render: the unified steps, both
 * historical branches at both money arms, the focus diagnostics and the
 * retired money question. It feeds the payload whitelist and the option check
 * on restore, so a step missing here is an answer silently dropped on save.
 */
const UNIFIED_QUESTION_STEPS: ApplicationStep[] = [
  STEP_CONTACT,
  STEP_WHERE,
  STEP_HELP_HOW,
  STEP_DFY_SCOPE,
  STEP_MAIN_PROBLEM,
  STEP_GOAL,
  STEP_MONEY_BAND,
  STEP_START_WINDOW,
  STEP_LOGISTICS_UNIFIED,
  STEP_CONTEXT_NOTE,
  STEP_SHOW_UP_UNIFIED,
]
const EVERY_STEP: ApplicationStep[] = [
  ...UNIFIED_QUESTION_STEPS,
  ...defaultStepsFor("price_disclosed"),
  ...defaultStepsFor("budget_bands"),
  ...dfyStepsFor("price_disclosed"),
  ...dfyStepsFor("budget_bands"),
  STEP_FOCUS_POST_BREAKUP,
  STEP_FOCUS_RELATIONSHIP,
  STEP_FOCUS_SOCIAL,
  STEP_INVESTMENT_LEGACY,
]

/**
 * Every question on every version and branch, deduped by id, the unified form
 * first.
 *
 * This one IS the union, and it has to be: the API route uses it to decide
 * which keys of an inbound payload to keep, so a narrower list would silently
 * discard answers before they ever reached the database. Nothing here implies a
 * question is asked of everyone: `validateApplication` checks only the steps
 * this answer set resolves to.
 */
export const APPLICATION_QUESTIONS: ApplicationQuestion[] = (() => {
  const seen = new Set<string>()
  const all: ApplicationQuestion[] = []
  // This is the payload whitelist, so a question missing here is an answer
  // silently dropped on save.
  for (const step of EVERY_STEP) {
    for (const q of step.questions) {
      if (seen.has(q.id)) continue
      seen.add(q.id)
      all.push(q)
    }
  }
  return all
})()

/** Every option value each question id accepts across a set of steps. */
function optionValuesFrom(steps: ApplicationStep[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>()
  for (const step of steps) {
    for (const q of step.questions) {
      if (!q.options) continue
      const values = map.get(q.id) ?? new Set<string>()
      for (const option of q.options) values.add(option.value)
      map.set(q.id, values)
    }
  }
  return map
}

/**
 * The option values a UNIFIED answer set may hold, and the values any version
 * ever accepted. Two versions share an id with DIFFERENT option sets (the age
 * question's youngest band, the show-up answers), so a restored answer is
 * checked against its own version: a historical "Under 25" carried into the
 * unified form would render no selected option, pass the step, and then fail
 * the submit, while the same answer on a historical row is perfectly valid.
 */
const UNIFIED_FORM_STEPS: ApplicationStep[] = [
  ...UNIFIED_QUESTION_STEPS,
  STEP_FOCUS_POST_BREAKUP,
  STEP_FOCUS_RELATIONSHIP,
  STEP_FOCUS_SOCIAL,
]
const UNIFIED_OPTION_VALUES = optionValuesFrom(UNIFIED_FORM_STEPS)
const ANY_VERSION_OPTION_VALUES = optionValuesFrom(EVERY_STEP)
/** Every question the unified form can ask, by id. */
const UNIFIED_QUESTION_BY_ID = new Map<string, ApplicationQuestion>(
  UNIFIED_FORM_STEPS.flatMap((step) => step.questions.map((q) => [q.id, q] as const)),
)

export type ApplicationAnswers = Record<string, string>

/** Widest age either slider can reach. Matches the control in the form. */
export const MATCH_AGE_MIN = 18
export const MATCH_AGE_MAX = 60

/** Parse an "min-max" age range, or null when it is missing or malformed. */
export function parseAgeRange(value: string | null | undefined): { min: number; max: number } | null {
  const match = /^(\d{2})-(\d{2})$/.exec((value ?? "").trim())
  if (!match) return null
  const min = Number(match[1])
  const max = Number(match[2])
  if (!Number.isInteger(min) || !Number.isInteger(max)) return null
  if (min < MATCH_AGE_MIN || max > MATCH_AGE_MAX || min > max) return null
  return { min, max }
}

/**
 * Server + client validation. Returns the list of missing/invalid question ids
 * (empty array == valid). Trims values; required text must be non-empty.
 *
 * Checks ONLY the questions on this applicant's own branch, resolved from the
 * `program` tag stored alongside the answers. Validating the union instead
 * would reject every mentorship application for not having answered the
 * Done-For-You questions it was never shown.
 */
export function validateApplication(answers: ApplicationAnswers): string[] {
  return applicationStepsForAnswers(answers).flatMap((step) => stepProblems(step, answers))
}

/** The missing or invalid question ids on one step. */
function stepProblems(step: ApplicationStep, answers: ApplicationAnswers): string[] {
  const problems: string[] = []
  for (const q of step.questions) {
    const raw = (answers[q.id] ?? "").toString().trim()
    if (q.required && !raw) {
      problems.push(q.id)
      continue
    }
    if (!raw) continue
    if (q.type === "email" && !EMAIL_RE.test(raw)) problems.push(q.id)
    if (q.type === "single" && q.options && !q.options.some((o) => o.value === raw)) {
      problems.push(q.id)
    }
    // Multi-select: the comma-joined value must resolve to at least one known
    // option, and every picked value must be a real option.
    if (q.type === "multi" && q.options) {
      const picked = raw.split(",").map((v) => v.trim()).filter(Boolean)
      const known = new Set(q.options.map((o) => o.value))
      if (picked.length === 0 || picked.some((v) => !known.has(v))) {
        problems.push(q.id)
      }
    }
    if (q.type === "agerange" && !parseAgeRange(raw)) problems.push(q.id)
  }
  return problems
}

/**
 * Where a resumed application picks up: its saved position, but never past
 * the first step that still has a missing or invalid answer. A row saved by an
 * earlier version of the form resumes into the unified questions, and its
 * saved index names a position in a different list, so trusting the index
 * alone would carry him past questions he has never seen and the submit would
 * then refuse the booked application for the answers he skipped.
 */
export function resumeStepIndex(answers: ApplicationAnswers, savedIndex: number | null | undefined): number {
  const steps = applicationStepsForAnswers(answers)
  const saved =
    typeof savedIndex === "number" && Number.isFinite(savedIndex)
      ? Math.max(0, Math.min(Math.floor(savedIndex), steps.length - 1))
      : 0
  const firstIncomplete = steps.findIndex((step) => stepProblems(step, answers).length > 0)
  return firstIncomplete < 0 ? saved : Math.min(saved, firstIncomplete)
}

/**
 * Lead-stage validation: a partial application only needs a contactable lead
 * (name + valid email). Used when the visitor finishes the contact step so an
 * abandoned application still reaches the team. Returns invalid ids.
 */
export function validateLead(answers: ApplicationAnswers): string[] {
  const problems: string[] = []
  if (!(answers.name ?? "").trim()) problems.push("name")
  const email = (answers.email ?? "").trim()
  if (!email || !EMAIL_RE.test(email)) problems.push("email")
  return problems
}

// Answer values renamed for copy compliance — map the old stored value to the
// current option value so historical rows still resolve to a label (the
// experience option "freeze" was renamed "lock_up" 2026-07-11; the banned word
// only ever lived as the stored value, never in the visible label). Exported so
// other readers of a raw historical answer (e.g. the lead-outreach email
// templates' {{detail}} personalization) resolve the same alias instead of
// re-deriving it and silently missing pre-rename rows.
export const LEGACY_VALUE_ALIASES: Record<string, string> = { freeze: "lock_up" }

/**
 * Drop any restored answer that no longer resolves to a valid option, so a
 * stale draft never dead-ends the applicant at Submit. When the form changes
 * (an option retired, a question flipped single→multi, the old free-text
 * "Something else" write-in removed 2026-07-18), a draft saved under the old
 * shape can hold a value that today's `validateApplication` rejects — and the
 * server 400s at Submit on the money path. Sanitizing on restore turns such a
 * value back into "unanswered" so the prospect simply re-picks. A single value
 * must match one option; a multi value keeps only its still-valid picks.
 */
export function sanitizeAnswers(answers: ApplicationAnswers): ApplicationAnswers {
  const byId = new Map(APPLICATION_QUESTIONS.map((q) => [q.id, q]))
  const unified = isUnifiedApplication(answers)
  const clean: ApplicationAnswers = {}
  for (const [id, value] of Object.entries(answers)) {
    // The focus stamp is a non-question key like `program`, but its value
    // shapes which steps render, so an unknown value is dropped rather than
    // carried into every later step resolution.
    if (id === "mentorship_focus") {
      if (isMentorshipFocus((value ?? "").trim())) clean[id] = (value ?? "").trim()
      continue
    }
    // The version stamp shapes the steps the same way, so only the current
    // stamp survives; anything else resolves as the historical form it was.
    if (id === "form_version") {
      if ((value ?? "").trim() === APPLICATION_FORM_VERSION) clean[id] = APPLICATION_FORM_VERSION
      continue
    }
    const q = byId.get(id)
    // A malformed age range is dropped for the same reason a retired option is:
    // it would 400 the submit rather than simply re-asking the question.
    if (q?.type === "agerange") {
      if (parseAgeRange(value)) clean[id] = (value ?? "").trim()
      continue
    }
    // Keep non-question keys (program, source, resume metadata) untouched.
    const known = (unified ? UNIFIED_OPTION_VALUES.get(id) : undefined) ?? ANY_VERSION_OPTION_VALUES.get(id)
    if (!q || !known || (q.type !== "single" && q.type !== "multi")) {
      clean[id] = value
      continue
    }
    const resolve = (v: string) => LEGACY_VALUE_ALIASES[v] ?? v
    if (q.type === "single") {
      if (known.has(resolve((value ?? "").trim()))) clean[id] = value
      continue
    }
    const kept = (value ?? "")
      .split(",")
      .map((v) => v.trim())
      .filter((v) => known.has(resolve(v)))
    if (kept.length) clean[id] = kept.join(",")
  }
  return clean
}

/** The label an option value shows, the renamed legacy values included. */
function optionLabel(q: ApplicationQuestion, value: string): string | undefined {
  const resolved = LEGACY_VALUE_ALIASES[value] ?? value
  return q.options?.find((o) => o.value === resolved)?.label
}

/** True when a man who gave `value` to `then` saw exactly the same question
 * and exactly the same answer label as `now` shows for it. */
function sameAnswerOnScreen(then: ApplicationQuestion, now: ApplicationQuestion, value: string): boolean {
  if (then.label !== now.label || then.type !== now.type) return false
  if (!now.options) return true
  const picked =
    now.type === "multi"
      ? (value ?? "").split(",").map((v) => v.trim()).filter(Boolean)
      : [(value ?? "").trim()]
  return (
    picked.length > 0 &&
    picked.every((v) => {
      const shown = optionLabel(then, v)
      return shown !== undefined && shown === optionLabel(now, v)
    })
  )
}

/**
 * An answer set an EARLIER version of the form saved (a resumed row, or a
 * local draft from before the unified form), carried into the unified form.
 *
 * NEVER A SILENT RE-MAP. An answer survives only where the unified form asks
 * the very same question and shows the very same answer label for it. The old
 * "Where would you most like to improve your dating life?" shares its key with
 * "Where do you want better results?" but is a different question, so its
 * answer is dropped and he is asked the new one, exactly as a historical
 * "Under 25" or "Maybe" is. His name, city, win and an unchanged age band carry
 * over. A key no unified question uses (the program tag, the source, a retired
 * question's answer) is kept as it is. The result carries the unified stamp; a
 * unified answer set comes back unchanged.
 */
export function answersCarriedIntoUnifiedForm(answers: ApplicationAnswers): ApplicationAnswers {
  if (isUnifiedApplication(answers)) return { ...answers }
  const asked = new Map<string, ApplicationQuestion>()
  for (const step of applicationStepsForAnswers(answers)) {
    for (const q of step.questions) asked.set(q.id, q)
  }
  const carried: ApplicationAnswers = {}
  for (const [id, value] of Object.entries(answers)) {
    const now = UNIFIED_QUESTION_BY_ID.get(id)
    if (!now) {
      carried[id] = value
      continue
    }
    const then = asked.get(id)
    if (then && sameAnswerOnScreen(then, now, value)) carried[id] = value
  }
  carried.form_version = APPLICATION_FORM_VERSION
  return carried
}

/**
 * True when a stored unified answer belongs to a question his own later
 * answers took off his path: the done-for-you scope of a man who picked the
 * dating-app work, chose "Just new photos", then switched to "Coach me". It is
 * no longer an answer to anything he was asked, so a surface that lists his
 * answers leaves it out rather than showing it as a leftover. A historical row
 * is never judged this way: every key it holds is shown as it was.
 */
export function isOffPathAnswer(answers: ApplicationAnswers, key: string): boolean {
  if (!isUnifiedApplication(answers) || !UNIFIED_QUESTION_BY_ID.has(key)) return false
  return !applicationStepsForAnswers(answers).some((step) => step.questions.some((q) => q.id === key))
}

/**
 * The answers an internal email renders for a stored lead ROW: every question
 * on any branch (the union, never the default list, which dropped a
 * Done-For-You lead's answers before the renderer saw them), the first-touch
 * source, the program tag, the money arm he was shown (so the step he stopped
 * on and the blank count resolve against HIS list), and the row's own name,
 * email and phone folded in.
 */
export function leadAnswersFromRow(
  raw: unknown,
  row: { name: string | null; email: string | null; phone: string | null; program: string | null },
): ApplicationAnswers {
  const out: ApplicationAnswers = {}
  if (raw && typeof raw === "object") {
    const source = raw as Record<string, unknown>
    for (const q of APPLICATION_QUESTIONS) {
      const value = source[q.id]
      if (typeof value === "string") out[q.id] = value
      else if (typeof value === "number") out[q.id] = String(value)
    }
    if (typeof source.source === "string") out.source = source.source
    if (typeof source.program === "string") out.program = source.program
    if (isMoneyArm(source.money_arm)) out.money_arm = source.money_arm
    // The two stamps the step resolution reads: without them a unified row
    // would render as a historical one and a focus row as the generic path.
    if (source.form_version === APPLICATION_FORM_VERSION) out.form_version = APPLICATION_FORM_VERSION
    if (isMentorshipFocus(source.mentorship_focus)) out.mentorship_focus = source.mentorship_focus
  }
  if (row.name) out.name = row.name
  if (row.email) out.email = row.email
  if (row.phone) out.phone = row.phone
  if (row.program) out.program = row.program
  return out
}

/** Human-readable value for an answer (maps select values to labels). */
export function displayAnswer(q: ApplicationQuestion, value: string): string {
  const raw = (value ?? "").toString().trim()
  if (!raw) return "—"
  if (q.type === "single" && q.options) {
    const resolved = LEGACY_VALUE_ALIASES[raw] ?? raw
    return q.options.find((o) => o.value === resolved)?.label ?? raw
  }
  if (q.type === "multi" && q.options) {
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => {
        const resolved = LEGACY_VALUE_ALIASES[v] ?? v
        return q.options!.find((o) => o.value === resolved)?.label ?? v
      })
      .join(", ")
  }
  if (q.type === "agerange") {
    const range = parseAgeRange(raw)
    return range ? `${range.min} to ${range.max}` : raw
  }
  return raw
}
